import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import pickingService from "../Services/pickingService";
import moment from "moment";
import { ArrowLeft } from "lucide-react";

export default function PickingView() {
  const { id } = useParams();
  const [picking, setPicking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPicking = async () => {
      try {
        const res = await pickingService.getSingle(id);
        if (res.responseCode === 200 && res.picking) {
          setPicking(res.picking);
        } else {
          setPicking(null);
        }
      } catch (error) {
        console.error("Error fetching picking:", error);
        setPicking(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPicking();
  }, [id]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-gray-600 text-lg">Loading...</p>
      </div>
    );

  if (!picking)
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <p className="text-gray-500 text-lg">No picking found</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md"
        >
          Go Back
        </button>
      </div>
    );

  // Quantity Total
  const totalQuantity =
    picking.products?.reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0,
    ) || 0;
  // quantityPlaced
  const totals = picking.products?.reduce(
    (acc, item) => {
      const qty = Number(item.quantityPlaced) || 0;
      const price = Number(item.price) || 0;

      acc.totalQuantity += qty;
      acc.totalPrice += price;
      acc.totalAmount += qty * price;

      return acc;
    },
    {
      totalQuantity: 0,
      totalPrice: 0,
      totalAmount: 0,
    },
  );

  return (
    <div className="mx-10 p-8 rounded-3xl border border-gray-200 shadow-lg bg-white min-h-[calc(100vh-48px)]">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-secondary">Picking Details</h1>
      </div>

      <div className="bg-white border shadow-md w-[60%] p-10 mt-10 rounded-3xl">
        <div className="flex flex-row gap-3 items-center">
          <div className="text-cyan-700 text-xl font-semibold flex flex-row items-center gap-2">
            <h1>{picking.company_name}</h1>
          </div>
        </div>
        <div className="grid grid-cols-2">
          <div>
            <div className="text-sm font-medium text-gray-400 mt-2">
              <p>
                Picking ID:{" "}
                <span className="font-semibold text-gray-700">
                  {picking.id}
                </span>
              </p>
            </div>
            <div className="text-sm font-medium text-gray-400 mt-2">
              <p>
                Created At:{" "}
                <span className="font-semibold text-gray-700">
                  {moment(picking.created_at).format("lll")}
                </span>
              </p>
            </div>
            <div className="text-sm font-medium text-gray-400 mt-2">
              <p>
                Picking Date:{" "}
                <span className="font-semibold text-gray-700">
                  {moment(picking.date).format("ll")}
                </span>
              </p>
            </div>
            <div className="text-sm font-medium text-gray-400 mt-2">
              <p>
                Sale:{" "}
                <span className="font-semibold text-gray-700">
                  {picking.order || "N/A"}
                </span>
              </p>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-400 mt-2">
              <p>
                Description:{" "}
                <span className="font-semibold text-gray-700">
                  {picking.description || "N/A"}
                </span>
              </p>
            </div>
            <div className="text-sm font-medium text-gray-400 mt-2">
              <p>
                Status:{" "}
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                    picking.status === "Pending"
                      ? "bg-red-100 text-red-600"
                      : "bg-green-100 text-green-600"
                  }`}
                >
                  {picking.status}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Picking Items Table */}
      <div className="flex flex-row justify-between mt-10">
        <div className="font-bold text-2xl text-secondary">Picking Items</div>
        <button
          onClick={() => setShowDetails((prev) => !prev)}
          className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:shadow transition-all duration-300"
        >
          <svg
            className={`w-4 h-4 transition-transform duration-300 ${showDetails ? "rotate-180" : ""}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
          </svg>
          {showDetails ? "Hide Details" : `Show Details`}
        </button>
      </div>
      <div
        className={`transition-all duration-500 overflow-hidden ${showDetails ? "opacity-100 mt-4" : "max-h-0 opacity-0"}`}
      >
        <div className="table-div">
          <table className="w-full">
            <thead>
              <tr className="border-y-2 text-left w-full text-black text-[10px]">
                <th className="p-3 text-center">ID</th>
                <th className="p-3 text-center">Maker</th>
                <th className="p-3 text-center">Part No.</th>
                <th className="p-3 text-center">Part Name</th>
                <th className="p-3 text-center">Quantity</th>
                <th className="p-3 text-center">Placed</th>
                <th className="p-3 text-center">Price</th>
                <th className="p-3 text-center">Total Price</th>
                <th className="p-3 text-center">HS Code</th>
                <th className="p-3 text-center">Origin</th>
                <th className="p-3 text-center">Box No.</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(picking.products) &&
              picking.products.length > 0 ? (
                picking.products.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b text-center hover:bg-gray-50 transition-colors"
                  >
                    <td className="p-3 text-center font-semibold text-blue-500">
                      {index + 1}.
                    </td>
                    <td className="p-3 text-center font-medium text-gray-800">
                      {item.maker}
                    </td>
                    <td className="p-3 text-center">{item.partnumber}</td>
                    <td className="p-3 text-center font-semibold">
                      {item.description || "-"}
                    </td>{" "}
                    {/* ✅ Part Name from backend */}
                    <td className="p-3 text-center">{item.quantity}</td>
                    <td className="p-3 text-center">{item.quantityPlaced}</td>
                    <td className="p-3 text-center">
                      {Number(item.price).toFixed(2)}
                    </td>
                    <td className="p-3 text-center">
                      {(
                        Number(item.quantityPlaced) * Number(item.price)
                      ).toFixed(2)}
                    </td>
                    <td className="p-3 text-center">{item.hscode}</td>
                    <td className="p-3 text-center">{item.origin || "-"}</td>
                    <td className="p-3 text-center">{item.box || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center p-6 text-gray-500">
                    No items found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex flex-row justify-center gap-4 mt-10 flex-wrap">
        {[
          {
            label: "Total Quantity",
            value: totalQuantity,
          },
          { label: "Total Placed", value: totals?.totalQuantity || 0 },
          {
            label: "Price",
            value: `${totals?.totalPrice.toFixed(2) || 0} AED`,
          },
          {
            label: "Total Price",
            value: `${totals?.totalAmount.toFixed(2) || 0} AED`,
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className="flex flex-col bg-gray-50 border border-gray-200 rounded-xl px-5 py-3"
          >
            <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">
              {stat.label}
            </span>
            <span className="text-sm font-bold text-gray-800 mt-1">
              {stat.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
